"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { MotionLink } from "@/components/ui/Motion";
import {
  TypographyH2,
  TypographyH3,
  TypographySmall,
} from "@/components/ui/typography";
import { URLS } from "@/lib/consts";
import { cls } from "@/lib/utils";
import { Image } from "../../Image";
import SectionBase from "../section-base";
import SectionHeader from "../section-header";

interface BuildRenovateItem {
  img: string;
  name: string;
  buttonName: string;
  url: string;
  desc: string;
  sub: string;
}

const OUR_BUILD_RENOVATE_PROCESS_SECTION_ID = "build-renovate-process";

const BUILD_RENOVATE_ITEMS: BuildRenovateItem[] = [
  {
    img: "projects/River Ranch/7-web-or-mls-130-01073_rru8vn",
    name: "Check Out Our Build Process",
    sub: "BUILD PROCESS",
    buttonName: "Discover Our Build Process",
    url: URLS.SERVICES.DESIGN_AND_BUILD,
    desc: "Bring your dream home to life with a tailored approach to custom home construction. From initial concepts to the final touches, we guide you every step of the way.",
  },
  {
    img: "projects/Sellers/16-print-QC-08800_hvcp8m",
    name: "Explore Our Renovation Process",
    sub: "RENOVATE PROCESS",
    buttonName: "Explore Our Renovation Process",
    url: URLS.SERVICES.DESIGN_AND_RENOVATE,
    desc: "Transform your existing space into something extraordinary. Our team specializes in thoughtful design and expert craftsmanship to create a home you'll love.",
  },
];

interface OurBuildRenovateProcessProperties {
  background?: string;
}

function OurBuildRenovateProcess({
  background = "bg-background",
}: OurBuildRenovateProcessProperties) {
  return (
    <SectionBase
      id={OUR_BUILD_RENOVATE_PROCESS_SECTION_ID}
      background={background}
    >
      <SectionHeader
        subtitle="OUR PROCESS"
        title="How We Bring Your Vision to Life"
        description="Whether you're building from the ground up or transforming an existing space, our proven process ensures a seamless experience from start to finish."
      />

      <div className={cls`flex flex-col gap-6 w-full mt-8`}>
        {BUILD_RENOVATE_ITEMS.map(
          ({ sub, img, name, url, desc, buttonName }) => (
            <Card
              key={name}
              className={cls`flex flex-1 flex-col md:flex-row`}
            >
              <CardHeader className={cls`flex-1 w-full md:p-10`}>
                <Image
                  className="rounded-md z-1"
                  wrapperClassName="rounded-md"
                  src={img}
                  alt={name}
                  fill
                  style={{ objectFit: "cover" }}
                />
              </CardHeader>

              <CardContent
                className={cls`flex flex-col flex-1 gap-5 p-5 md:p-10`}
              >
                <TypographyH3 className="font-bold text-primary">
                  {sub}
                </TypographyH3>
                <TypographyH2 className="border-b-0">{name}</TypographyH2>
                <TypographySmall>{desc}</TypographySmall>
                <Button className={cls`md:w-fit`} asChild>
                  <MotionLink href={url}>{buttonName}</MotionLink>
                </Button>
              </CardContent>
            </Card>
          ),
        )}
      </div>
    </SectionBase>
  );
}

export default OurBuildRenovateProcess;
